import { FC, useCallback } from 'react';

import { useHideCurrency } from '@pp/core/src/store/compositeSelectors/useHideCurrency';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { roundNumber } from '@pp/core/src/utils/number';

import { useValidBetInputValue } from '../../components/gameGrid/hooks/useValidBetInputValue';
import { useGameDispatch } from '../../store/GameStore';
import { betStepperActions } from '../../store/slices/betStepper/slice';
import { highflyerGameStateActions } from '../../store/slices/highflyerGame/slice';

import { VirtualKeypad } from './VirtualKeypad';

export interface VirtualKeypadProps {
  betCode: number;
}

export const VirtualKeypadConnected: FC<VirtualKeypadProps> = ({ betCode }) => {
  const betLimits = useBetSlice.betLimits() ?? {};
  const tableMinLimits = betLimits[betCode].min;
  const tableMaxLimits = betLimits[betCode].max;
  const currency = useGameStateSlice.getCurrency() ?? '';
  const currentLanguage = useSettingsSlice.currentLanguage();
  const hideCurrency = useHideCurrency();
  const { betInputValue, betInputAmount } = useValidBetInputValue(betCode);
  const dispatch = useGameDispatch();

  const handleDispatch = useCallback((updatedPriceValue: string) => {
    const finalBetInput = updatedPriceValue;
    dispatch(
      betStepperActions.setBetInputValue({
        betId: betCode,
        value: finalBetInput.toString(),
      })
    );
  }, []);

  const handleQuickBet = useCallback(
    (betId: number, priceValue: string): void => {
      dispatch(
        betStepperActions.setBetInputValue({
          betId: betId,
          value: roundNumber(Number(priceValue), 2).toFixed(2),
          isBetFinalized: true,
        })
      );
      dispatch(highflyerGameStateActions.setToggleKeypad({ keypad: false, betCode: betCode }));
    },
    [dispatch]
  );

  return (
    <VirtualKeypad
      betCode={betCode}
      maxLimits={tableMaxLimits}
      minLimits={tableMinLimits}
      currency={currency}
      currentLanguage={currentLanguage}
      hideCurrency={hideCurrency}
      betLabel="BETLABEL"
      betInputAmount={betInputAmount}
      betInputValue={betInputValue}
      handleQuickBet={handleQuickBet}
      handleDispatch={handleDispatch}
      showFormatter={false}
    />
  );
};
